
import { supabase } from './supabase';
import { Lock, LockType, CreateLockDTO, Lead } from '../types';

// Helper Mapping to handle the PROFILE: convention and JSON config
const mapToLock = (data: any): Lock => {
  let leadsCount = 0;
  if (data.leads && Array.isArray(data.leads) && data.leads[0]) {
      leadsCount = data.leads[0].count;
  } else {
      leadsCount = data.leads_count || 0;
  }

  let socialUrl = data.social_url;
  let socialType: 'profile' | 'post' | undefined = undefined;

  if (socialUrl) {
      if (socialUrl.startsWith('PROFILE:')) {
          socialType = 'profile';
          socialUrl = socialUrl.replace('PROFILE:', '');
      } else {
          socialType = 'post';
      }
  }

  return {
    id: data.id,
    slug: data.slug,
    targetUrl: data.target_url,
    type: data.type as LockType,
    title: data.title,
    imageUrl: data.image_url,
    buttonText: data.button_text,
    socialUrl: socialUrl,
    socialType: socialType,
    config: data.config || {}, // Map the JSON config safely
    viewsCount: data.views_count || 0,
    leadsCount: leadsCount,
    createdAt: new Date(data.created_at).getTime(),
  };
};

// --- MOCK DATA GENERATOR FOR DEMO ACCOUNT ---
// CONSTANTS
const MOCK_LEADS_COUNT_1 = 2432;
const MOCK_LEADS_COUNT_2 = 850;

const getMockLocks = (userId: string): Lock[] => [
    {
        id: 'mock-1', slug: 'growth-guide', targetUrl: 'https://example.com', type: LockType.FILE,
        title: 'Q3 Growth Strategy.pdf', imageUrl: 'https://images.unsplash.com/photo-1614850523459-c2f4c699c52e?q=80&w=2070',
        buttonText: 'Unlock PDF', viewsCount: 5840, leadsCount: MOCK_LEADS_COUNT_1, createdAt: Date.now() - 604800000, // 7 days ago
        config: { tip: { enabled: true, amount: 5 }, fomo: { enabled: true, type: 'timer', text: 'Ends in 2h' } }
    },
    {
        id: 'mock-2', slug: 'design-system', targetUrl: 'https://example.com', type: LockType.LINK,
        title: 'Figma Design System v2', imageUrl: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?q=80&w=1974',
        buttonText: 'Access Figma', viewsCount: 1205, leadsCount: MOCK_LEADS_COUNT_2, createdAt: Date.now() - 400000000,
        config: { security: { b2bOnly: true } }
    },
    {
        id: 'mock-3', slug: 'newsletter-archive', targetUrl: 'https://example.com', type: LockType.LINK,
        title: 'Newsletter Archive (VIP)', imageUrl: 'https://images.unsplash.com/photo-1557200130-9727ddbe71ff?q=80&w=2069',
        buttonText: 'Read Now', viewsCount: 150, leadsCount: 45, createdAt: Date.now() - 100000000,
        socialUrl: 'twitter.com/demo', socialType: 'post'
    }
];

const getMockLeads = (userId: string): Lead[] => {
    const leads: Lead[] = [];
    const domains = ['gmail.com', 'startup.io', 'agency.co', 'tech.net', 'outlook.com', 'apple.com', 'stripe.com'];
    
    // Generate realistic leads for Lock 1 (High volume)
    // Distribution: Ramp up over 7 days
    const now = Date.now();
    const oneDay = 86400000;

    for(let i=0; i < MOCK_LEADS_COUNT_1; i++) {
        // Skew towards more recent (exponential-ish distribution)
        const daysBack = Math.floor(Math.pow(Math.random(), 3) * 7); 
        const timeOffset = (daysBack * oneDay) + Math.random() * oneDay;
        
        leads.push({
            id: `lead-1-${i}`, 
            lockId: 'mock-1', 
            email: `user${i}@${domains[Math.floor(Math.random() * domains.length)]}`,
            createdAt: now - timeOffset,
            deviceType: Math.random() > 0.4 ? 'mobile' : 'desktop',
            lockTitle: 'Q3 Growth Strategy.pdf'
        });
    }

    // Generate leads for Lock 2
    for(let i=0; i < MOCK_LEADS_COUNT_2; i++) {
        const daysBack = Math.floor(Math.random() * 7);
        const timeOffset = (daysBack * oneDay) + Math.random() * oneDay;

        leads.push({
            id: `lead-2-${i}`, 
            lockId: 'mock-2', 
            email: `designer${i}@${domains[Math.floor(Math.random() * domains.length)]}`,
            createdAt: now - timeOffset,
            deviceType: 'desktop', // Designers mostly on desktop
            lockTitle: 'Figma Design System v2'
        });
    }

    return leads.sort((a, b) => b.createdAt - a.createdAt);
};
// -------------------------------------------

export const lockService = {
  // Récupérer les locks de l'utilisateur connecté
  getUserLocks: async (userId: string): Promise<Lock[]> => {
    // CHECK FOR DEMO ACCOUNT
    const { data: { user } } = await supabase.auth.getUser();
    if (user?.email === 'mart.vion@gmail.com') {
        return getMockLocks(userId);
    }

    const { data, error } = await supabase
      .from('locks')
      .select('*, leads(count)') 
      .eq('user_id', userId)
      .neq('slug', 'landing-page')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []).map(mapToLock);
  },

  // Get ALL leads across ALL locks for a user
  getAllUserLeads: async (userId: string): Promise<Lead[]> => {
    // CHECK FOR DEMO ACCOUNT
    const { data: { user } } = await supabase.auth.getUser();
    if (user?.email === 'mart.vion@gmail.com') {
        return getMockLeads(userId);
    }

    const { data, error } = await supabase
      .from('leads')
      .select('*, locks!inner(user_id, title, slug)')
      .eq('locks.user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return (data || []).map((l: any) => ({
        id: l.id,
        lockId: l.lock_id,
        email: l.email,
        createdAt: new Date(l.created_at).getTime(),
        deviceType: l.device_type || 'desktop',
        lockTitle: l.locks?.title || 'Unknown Lock'
    }));
  },

  getLockDetails: async (lockId: string): Promise<Lock | null> => {
    // DEMO HANDLING
    if (lockId.startsWith('mock-')) {
        const mocks = getMockLocks('demo');
        return mocks.find(m => m.id === lockId) || null;
    }

    const { data, error } = await supabase
      .from('locks')
      .select('*, leads(count)')
      .eq('id', lockId)
      .single();

    if (error) return null;
    return mapToLock(data);
  },

  getLockLeads: async (lockId: string): Promise<Lead[]> => {
    // DEMO HANDLING
    if (lockId.startsWith('mock-')) {
        const allMockLeads = getMockLeads('demo');
        return allMockLeads.filter(l => l.lockId === lockId);
    }

    const { data, error } = await supabase
      .from('leads')
      .select('*')
      .eq('lock_id', lockId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return (data || []).map((l: any) => ({
        id: l.id,
        lockId: l.lock_id,
        email: l.email,
        createdAt: new Date(l.created_at).getTime(),
        deviceType: l.device_type || 'desktop'
    }));
  },

  getLockBySlug: async (slug: string): Promise<Lock | null> => {
    // Mock slugs check
    if (['growth-guide', 'design-system', 'newsletter-archive'].includes(slug)) {
         const mocks = getMockLocks('demo');
         return mocks.find(m => m.slug === slug) || null;
    }

    const { data, error } = await supabase
      .from('locks')
      .select('*')
      .eq('slug', slug)
      .single();

    if (error) return null;
    return mapToLock(data);
  },

  // Specific System Tracker for Landing Page
  trackLandingView: async () => {
      try {
          const { data, error } = await supabase
              .from('locks')
              .select('id')
              .eq('slug', 'landing-page')
              .single();
          
          if (data) {
              await lockService.incrementView(data.id);
          }
      } catch (e) {
          console.error("Tracking error", e);
      }
  },

  incrementView: async (lockId: string) => {
    if (lockId.startsWith('mock-')) return; // Don't track mocks

    try {
        const { error } = await supabase.rpc('increment_views', { row_id: lockId });
        
        if (error) {
            const { data: lock } = await supabase
                .from('locks')
                .select('views_count')
                .eq('id', lockId)
                .single();
            
            if (lock) {
                const newCount = (lock.views_count || 0) + 1;
                await supabase.from('locks').update({ views_count: newCount }).eq('id', lockId);
            }
        }
    } catch (e) {
        console.error("Failed to increment view", e);
    }
  },

  createLock: async (userId: string, data: CreateLockDTO, files?: File[]): Promise<Lock> => {
    let targetUrl = data.targetUrl;

    if (data.type === LockType.FILE && files && files.length > 0) {
      const file = files[0]; 
      const fileExt = file.name.split('.').pop();
      const fileName = `${userId}/${Date.now()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage
        .from('lock-files')
        .upload(fileName, file);

      if (uploadError) throw uploadError;
      targetUrl = fileName;
    }

    let storedSocialUrl = data.socialUrl;
    if (data.socialUrl && data.socialType === 'profile') {
        storedSocialUrl = `PROFILE:${data.socialUrl}`;
    }

    const slug = (data as any).slug || Math.random().toString(36).substring(2, 8);
    
    const payload: any = {
        user_id: userId,
        slug: slug,
        type: data.type,
        target_url: targetUrl,
        title: data.title,
        image_url: data.imageUrl,
        button_text: data.buttonText,
        config: data.config
    };

    if (storedSocialUrl) {
        payload.social_url = storedSocialUrl;
    }

    try {
        const { data: newLock, error } = await supabase
        .from('locks')
        .insert(payload)
        .select()
        .single();

        if (error) throw error;
        return mapToLock(newLock);

    } catch (error: any) {
        if (error.code === '42703' || error.message?.includes('config') || error.message?.includes('column')) {
            delete payload.config;
            const { data: fallbackLock, error: fallbackError } = await supabase
                .from('locks')
                .insert(payload)
                .select()
                .single();
            
            if (fallbackError) throw fallbackError;
            return mapToLock(fallbackLock);
        }
        throw error;
    }
  },

  updateLock: async (lockId: string, updates: Partial<Lock> & { socialType?: 'profile'|'post' }): Promise<Lock> => {
    if (lockId.startsWith('mock-')) throw new Error("Cannot update demo locks");

    const dbUpdates: any = {};
    
    if (updates.title !== undefined) dbUpdates.title = updates.title;
    if (updates.config !== undefined) dbUpdates.config = updates.config; 
    
    if (updates.socialUrl !== undefined) {
        if (updates.socialType === 'profile') {
            dbUpdates.social_url = `PROFILE:${updates.socialUrl}`;
        } else {
            dbUpdates.social_url = updates.socialUrl; 
        }
    }

    const { data, error } = await supabase
      .from('locks')
      .update(dbUpdates)
      .eq('id', lockId)
      .select()
      .single();

    if (error) throw error;
    return mapToLock(data);
  },

  captureLead: async (lockId: string, email: string) => {
    if (lockId.startsWith('mock-')) {
        // Mock success
        return;
    }

    const { error } = await supabase
      .from('leads')
      .insert({
        lock_id: lockId,
        email: email,
        device_type: window.innerWidth < 768 ? 'mobile' : 'desktop'
      });

    if (error) throw error;
    
    try {
      const { error: rpcError } = await supabase.rpc('increment_leads', { row_id: lockId });
      if (rpcError) {
         const { data: lock } = await supabase.from('locks').select('leads_count').eq('id', lockId).single();
         if (lock) {
             const newCount = (lock.leads_count || 0) + 1;
             await supabase.from('locks').update({ leads_count: newCount }).eq('id', lockId);
         }
      }
    } catch (e) {
      console.log("Could not update lock counter", e);
    }
  },

  getFileDownloadUrl: async (path: string) => {
    const { data, error } = await supabase.storage
      .from('lock-files')
      .createSignedUrl(path, 3600);
    if (error) throw error;
    return data.signedUrl;
  },
  
  deleteLock: async (id: string) => {
      if (id.startsWith('mock-')) throw new Error("Cannot delete demo locks");
      const { error } = await supabase.from('locks').delete().eq('id', id);
      if(error) throw error;
  }
};
